<!-- frontend/src/components/notifications/NotificationCard.vue -->
<template>
  <article
    class="notif-card"
    :class="[
      `notif-card--${notification.kind ?? 'info'}`,
      `notif-card--priority-${notification.priority}`,
      `notif-card--status-${notification.status}`,
      `notif-card--category-${notification.category || 'other'}`,
    ]"
  >
    <div class="notif-card__indicator" :aria-hidden="true"></div>

    <span class="notif-card__icon" aria-hidden="true">
      <component :is="icon" :size="20" />
    </span>

    <div
      class="notif-card__main"
      role="button"
      tabindex="0"
      @click="onBodyClick"
      @keydown.enter="onBodyClick"
      @keydown.space.prevent="onBodyClick"
    >
      <div class="notif-card__head">
        <strong class="notif-card__title">{{ displayTitle }}</strong>
        <span class="notif-card__time">{{ relativeTime }}</span>
      </div>
      <!-- Баг 5.2: при раскрытии (клик по телу) показываем полный текст без
           обрезки в 2 строки — это и есть «просмотр содержимого», без навигации. -->
      <p class="notif-card__body" :class="{ 'notif-card__body--expanded': expanded }">
        {{ displayBody }}
      </p>
      <button
        v-if="notification.cta"
        type="button"
        class="notif-card__cta"
        @click.stop="onCtaClick"
      >
        {{ notification.cta.label }}
      </button>
    </div>

    <button
      v-if="canDismiss"
      type="button"
      class="notif-card__dismiss"
      :aria-label="'Скрыть уведомление'"
      @click.stop="onDismissClick"
    >
      <X :size="18" aria-hidden="true" />
    </button>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  Bell,
  CalendarClock,
  CalendarDays,
  CreditCard,
  LifeBuoy,
  UserCheck,
  X,
} from 'lucide-vue-next';
import type { RawNotification } from '@/shared/types/notification';

// `expanded` (баг 5.2) — раскрыта ли карточка в режиме просмотра содержимого;
// управляется родителем (страница/дропдаун), по умолчанию свёрнута.
const props = defineProps<{ notification: RawNotification; expanded?: boolean }>();
const emit = defineEmits<{
  (e: 'act', publicId: string, href: string): void;
  (e: 'dismiss', publicId: string): void;
  (e: 'open', publicId: string): void;
}>();

const ICON_BY_CATEGORY = {
  bookings: CalendarClock,
  events: CalendarDays,
  mentor_app: UserCheck,
  payments: CreditCard,
  support: LifeBuoy,
  other: Bell,
};

const stripHtml = (value: string): string =>
  value.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();

const icon = computed(() =>
  ICON_BY_CATEGORY[props.notification.category] ?? ICON_BY_CATEGORY.other
);

const fallbackTitle = computed(() => 'Уведомление');
const fallbackBody = computed(() =>
  stripHtml(props.notification.message ?? ''),
);
const displayTitle = computed(() =>
  stripHtml(props.notification.title || fallbackTitle.value),
);
const displayBody = computed(() =>
  stripHtml(props.notification.body || fallbackBody.value),
);

const canDismiss = computed(() =>
  !['acted', 'dismissed', 'expired'].includes(props.notification.status),
);

const relativeTime = computed(() => {
  const ts = new Date(props.notification.created_at).getTime();
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'только что';
  if (m < 60) return `${m} мин назад`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} ч назад`;
  const d = Math.floor(h / 24);
  return `${d} дн назад`;
});

function onCtaClick() {
  if (!props.notification.cta) return;
  emit('act', props.notification.public_id, props.notification.cta.href);
}

function onBodyClick() {
  emit('open', props.notification.public_id);
}

function onDismissClick() {
  emit('dismiss', props.notification.public_id);
}
</script>

<style scoped>
.notif-card {
  --notif-card-accent: #7B65F7;
  --notif-card-accent-rgb: 123, 101, 247;
  display: grid;
  grid-template-columns: 4px 40px minmax(0, 1fr) auto;
  gap: 12px;
  align-items: flex-start;
  padding: 16px 18px;
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(var(--notif-card-accent-rgb), 0.06) 0%, #ffffff 78%);
  border: 1px solid rgba(var(--notif-card-accent-rgb), 0.18);
  box-shadow: 0 12px 24px rgba(31, 27, 45, 0.08);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}
.notif-card:hover {
  transform: translateY(-2px);
  border-color: rgba(var(--notif-card-accent-rgb), 0.34);
  box-shadow: 0 16px 32px rgba(31, 27, 45, 0.12);
}

.notif-card__indicator {
  background: rgba(var(--notif-card-accent-rgb), 0.84);
  align-self: stretch;
  min-height: 42px;
  border-radius: 999px;
}

.notif-card--status-pending {
  background: linear-gradient(135deg, rgba(var(--notif-card-accent-rgb), 0.1) 0%, #ffffff 78%);
}

.notif-card--priority-high {
  --notif-card-accent: #e11d48;
  --notif-card-accent-rgb: 225, 29, 72;
  background: linear-gradient(135deg, #fff1f6 0%, #ffffff 84%);
}

.notif-card--status-acted,
.notif-card--status-dismissed,
.notif-card--status-expired {
  opacity: 0.6;
}

.notif-card--category-payments {
  --notif-card-accent: #22c55e;
  --notif-card-accent-rgb: 34, 197, 94;
}

.notif-card--category-support {
  --notif-card-accent: #f59e0b;
  --notif-card-accent-rgb: 245, 158, 11;
}

.notif-card--category-mentor_app {
  --notif-card-accent: #14b8a6;
  --notif-card-accent-rgb: 20, 184, 166;
}

.notif-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 14px;
  background: rgba(var(--notif-card-accent-rgb), 0.1);
  color: var(--notif-card-accent);
  box-shadow: inset 0 0 0 1px rgba(var(--notif-card-accent-rgb), 0.12);
}

.notif-card__main {
  min-width: 0;
  overflow: hidden;
  outline: none;
}
.notif-card__head {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: baseline;
}
.notif-card__title {
  min-width: 0;
  color: #1f1b2d;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.3;
  overflow-wrap: anywhere;
  word-break: normal;
}
.notif-card__time {
  color: #6B7280;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
}
.notif-card__body {
  margin: 6px 0 12px;
  color: #4b5563;
  font-size: 14px;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
  word-break: normal;
}
/* Баг 5.2: раскрытая карточка показывает текст целиком (без clamp). */
.notif-card__body--expanded {
  display: block;
  -webkit-line-clamp: unset;
  overflow: visible;
}
.notif-card__cta {
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  max-width: 100%;
  padding: 10px 14px;
  border: 1px solid rgba(var(--notif-card-accent-rgb), 0.2);
  border-radius: 12px;
  background: rgba(var(--notif-card-accent-rgb), 0.08);
  color: #1f1b2d;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.3;
  cursor: pointer;
  transition: background 0.16s ease, border-color 0.16s ease, transform 0.16s ease;
}
.notif-card__cta:hover {
  background: rgba(var(--notif-card-accent-rgb), 0.14);
  border-color: rgba(var(--notif-card-accent-rgb), 0.32);
  transform: translateY(-1px);
}
.notif-card__dismiss {
  align-self: start;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 999px;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: background 0.16s ease, border-color 0.16s ease, color 0.16s ease;
}
.notif-card__dismiss:hover {
  background: rgba(var(--notif-card-accent-rgb), 0.08);
  border-color: rgba(var(--notif-card-accent-rgb), 0.16);
  color: #1f1b2d;
}

.notif-card__main:focus-visible,
.notif-card__dismiss:focus-visible,
.notif-card__cta:focus-visible {
  outline: 2px solid rgba(var(--notif-card-accent-rgb), 0.55);
  outline-offset: 2px;
}

@media (max-width: 600px) {
  .notif-card {
    position: relative;
    grid-template-columns: 3px 36px minmax(0, 1fr);
    gap: 10px;
    padding: 14px;
    border-radius: 16px;
  }

  .notif-card__icon {
    width: 36px;
    height: 36px;
    border-radius: 12px;
  }

  .notif-card__head {
    grid-template-columns: minmax(0, 1fr);
    padding-right: 30px;
    gap: 3px;
  }

  .notif-card__time {
    font-size: 11px;
  }

  .notif-card__body {
    -webkit-line-clamp: 3;
    font-size: 13px;
  }

  .notif-card__dismiss {
    position: absolute;
    top: 8px;
    right: 8px;
  }
}
</style>
